<template>
    <section class="directions-map">
        <div id="container">
            <div>
                <div class="tabs">
                    <!-- Walking first and by default: visitors are on foot between
                         buildings. Transit only appears when the route has no
                         intermediate stops, because the Directions service
                         rejects waypoints in TRANSIT mode. -->
                    <button
                        v-for="mode in availableModes"
                        :key="mode.value"
                        type="button"
                        class="gray-tag"
                        :class="{ active: travelMode === mode.value }"
                        :aria-pressed="String(travelMode === mode.value)"
                        @click="selectTravelMode(mode.value)">
                        {{ mode.label }}
                    </button>
                    <button type="button" class="close-directions" v-if="canToggleDirections" @click="hideDirections"><span aria-hidden="true">&#x2715;</span> Hide Directions</button>
                    <div class="calculating" v-show="loading">
                        <LoadingSpinner />
                        <p class="body-small heavy">Calculating...</p>
                    </div>
                </div>
                <div id="map"></div>
                <div id="sidebar" v-show="!loading">
                    <p>Total Distance: <span id="total"></span></p>
                    <div id="panel"></div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
    import mapStyles from '@/helpers/mapStyles.json';
    const emit = defineEmits(['hideDirections']);

    const props = defineProps({
        cards: {
            type: Array,
            required: false,
            default: [],
        },
        canToggleDirections: {
            type: Boolean,
            required: false,
            default: false,
        },
    });
    const { cards } = toRefs(props);

    const loading = ref(true);
    const el = ref();
    const travelMode = ref('WALKING');

    const userCoords = ref([]);
    const geolocationComplete = ref(false);

    watch([cards, travelMode], ([newcards, newtravelMode], [prevcards, prevtravelMode]) => {
        initMap();
    });

    const startingPoint = computed(() => {
        if (userCoords.value.length) {
            let coords = new google.maps.LatLng(userCoords.value[0], userCoords.value[1]);
            return coords;
        } else if (cards.value?.[0]?.fields?.['Site Address']) {
            return `${cards.value?.[0]?.fields?.['Site Address']}, Chicago, IL`;
        } else {
            let formattedCoords = formatStopsGeo(cards.value?.[0]);
            return new google.maps.LatLng(formattedCoords[0], formattedCoords[1]);
        }
    });

    const stoppingPoint = computed(() => {
        if (cards.value?.[cards.value.length - 1]?.fields?.['Site Address']) {
            return `${cards.value?.[cards.value.length - 1]?.fields?.['Site Address']}, Chicago, IL`;
        } else {
            let formattedCoords = formatStopsGeo(cards.value?.[cards.value.length - 1]);
            return new google.maps.LatLng(formattedCoords[0], formattedCoords[1]);
        }
    });

    const wayPoints = computed(() => {
        let clone = JSON.parse(JSON.stringify(cards.value));
        let newList = [];

        if (clone.length >= 3) {
            if (!userCoords.value.length) {
                clone.shift();
            }
            clone.pop();

            clone.forEach((site) => {
                const isSite = site?.fields?.['Site Address'];
                let coords;
                if (isSite) {
                    coords = `${site?.fields?.['Site Address']}, Chicago, IL`;
                } else {
                    let formattedCoords = formatStopsGeo(site);
                    coords = new google.maps.LatLng(formattedCoords[0], formattedCoords[1]);
                }

                newList.push({
                    location: coords,
                });
            });
            return newList;
        } else {
            return newList;
        }
    });

    const availableModes = computed(() => {
        const modes = [
            { value: 'WALKING', label: 'Walking' },
            { value: 'TRANSIT', label: 'Transit' },
            { value: 'DRIVING', label: 'Driving' },
        ];
        // Counted from the cards rather than read from wayPoints, which builds
        // google.maps objects and so cannot run during SSR.
        const hasWaypoints = (cards.value?.length ?? 0) >= 3;
        return hasWaypoints ? modes.filter((mode) => mode.value !== 'TRANSIT') : modes;
    });

    // A route that gains a stop while Transit is selected falls back to walking.
    watch(availableModes, (modes) => {
        if (!modes.some((mode) => mode.value === travelMode.value)) travelMode.value = 'WALKING';
    });

    function formatStopsGeo(site) {
        let splitCoords = site?.fields?.['Geocoordinates'].split(',');
        let lat = cleanCoords(splitCoords[0]);
        let long = cleanCoords(splitCoords[1]);
        return [lat, long];
    }

    function cleanCoords(item) {
        return parseFloat(item.trim());
    }

    function initMap() {
        if (import.meta.client) {
            loading.value = true;
            document.getElementById('panel').innerHTML = '';

            const map = new google.maps.Map(document.getElementById('map'), {
                zoom: 10,
                styles: mapStyles,
                center: { lat: 41.8827, lng: -87.6207 }, // Cloud Gate.
            });

            map.addListener('tilesloaded', function () {
                if (loading.value === true) {
                    loading.value = false;
                }
            });

            const directionsService = new google.maps.DirectionsService();
            const directionsRenderer = new google.maps.DirectionsRenderer({
                draggable: true,
                map,
                panel: document.getElementById('panel'),
            });

            directionsRenderer.addListener('directions_changed', () => {
                const directions = directionsRenderer.getDirections();

                if (directions) {
                    computeTotalDistance(directions);
                }
            });

            displayRoute(startingPoint.value, stoppingPoint.value, directionsService, directionsRenderer, travelMode.value);
        }
    }

    function displayRoute(origin, destination, service, display, mode) {
        if (import.meta.client) {
            service
                .route({
                    origin: origin,
                    destination: destination,
                    waypoints: wayPoints.value,
                    travelMode: google.maps.TravelMode[mode],
                    avoidTolls: true,
                })
                .then((result) => {
                    display.setDirections(result);
                })
                .catch((e) => {
                    alert('Could not display directions due to: ' + e);
                });
        }
    }

    function computeTotalDistance(result) {
        if (import.meta.client) {
            let total = 0;
            const myroute = result.routes[0];

            if (!myroute) {
                return;
            }

            for (let i = 0; i < myroute.legs.length; i++) {
                total += myroute.legs[i].distance.value;
            }

            total = total / 1000;
            document.getElementById('total').innerHTML = total + ' km';
        }
    }

    function firstInit() {
        setTimeout(() => {
            initMap();

            const options = {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0,
            };
        }, 1000);
    }

    function success(pos) {
        const crd = pos.coords;
        userCoords.value = [crd.latitude, crd.longitude];
        geolocationComplete.value = true;
        firstInit();
    }

    function error(err) {
        console.log(`Geolocation error: (${err.code}): ${err.message}`);
        geolocationComplete.value = true;
        firstInit();
    }

    const hideDirections = () => {
        emit('hideDirections');
    };

    // Destination stop for this route; used as the site_id on the directions_opened event since
    // this embedded multi-stop map (used for tours) doesn't represent a single site.
    function selectTravelMode(mode) {
        travelMode.value = mode;
        const destination = cards.value?.[cards.value.length - 1];
        if (destination?.id) {
            const { track } = useAnalytics();
            track('directions_opened', {
                site_id: destination.id,
                site_name: destination.fields?.['Site'] || '',
                mode,
            });
        }
    }

    onMounted(() => {
        if (import.meta.client) {
            navigator.geolocation.getCurrentPosition(success, error);
        }
    });
</script>

<style lang="scss" scoped>
    #container {
        height: 100%;

        // Thumb-sized: at least 44px tall with 12px type (the global
        // .gray-tag is a ~21px box on phones).
        .gray-tag {
            display: inline-flex;
            align-items: center;
            min-height: 44px;
            padding: 0 14px;
            font-size: 12px;
            line-height: 12px;
            margin-right: 10px !important;
            margin-bottom: 23px;

            &.active {
                background-color: black;
                color: white;
            }
        }
    }

    #sidebar {
        flex-basis: 15rem;
        flex-grow: 1;
        padding: 1rem;
        max-width: none;
        height: 100%;
        box-sizing: border-box;
        overflow: auto;
        width: 100%;
    }

    #map {
        flex-basis: 0;
        flex-grow: 4;
        height: 300px;
    }

    #sidebar {
        /* background-color: pink;
  	color: white!important; */
        border: 1px solid lightgrey;
        border-top: 0;
    }

    .adp .warnbox-content,
    .warnbox,
    .adp-warnbox {
        visibility: hidden !important;
        height: 0 !important;
        width: 0 !important;
    }

    .directions-map {
        .tabs {
            position: relative;
            display: flex;
            align-items: flex-start;
        }
        .calculating {
            position: absolute;
            top: calc(100% + 5px);
            left: 0;
            right: 0;
            text-align: center;
            width: 100%;
            .lds-dual-ring {
                margin-left: -20px;
                margin-bottom: 13px;
            }
        }

        .close-directions {
            display: inline-block;
            font-size: 12px;
            font-weight: 800;
            line-height: 12px;
            letter-spacing: 0.72px;
            text-transform: uppercase;
            min-height: 44px;
            padding: 0;
            margin-left: auto;
        }
    }
</style>
